import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSession } from '@/hooks/useSession';
import { listAdminUsers, patchArtifact, type AdminUser, type Artifact } from '@/lib/api';
import { listTransferableOwnerUsers } from '@/lib/owner-transfer-users';

interface OwnerTransferControlProps {
  artifact: Artifact;
}

/**
 * Backend ownership transfer is admin-only. Non-admins do not see this control
 * at all, matching the server gate instead of rendering a button that always
 * returns 403.
 */
export function OwnerTransferControl({
  artifact,
}: OwnerTransferControlProps): React.ReactElement | null {
  const queryClient = useQueryClient();
  const { isAdmin } = useSession();
  const [nextOwnerId, setNextOwnerId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const usersQuery = useQuery<AdminUser[]>({
    queryKey: ['admin', 'users'],
    queryFn: listAdminUsers,
    enabled: isAdmin,
    staleTime: 60_000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (ownerId: string) => patchArtifact(artifact.id, { ownerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id] });
      void queryClient.invalidateQueries({ queryKey: ['artifact', artifact.id, 'activity'] });
      void queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setNextOwnerId('');
    },
    onError: err => {
      setError(err.message);
    },
  });

  if (!isAdmin) return null;

  function handleTransfer(): void {
    setError(null);
    const ownerId = nextOwnerId.trim();
    if (!ownerId) {
      setError('Owner ID is required');
      return;
    }
    if (ownerId === artifact.ownerId) {
      setError('That user is already the owner');
      return;
    }
    mutation.mutate(ownerId);
  }

  const users = listTransferableOwnerUsers(usersQuery.data ?? [], artifact.ownerId);

  return (
    <div className="flex flex-col gap-1">
      <span className="text-text-secondary">Owner (admin only)</span>
      <select
        value={nextOwnerId}
        onChange={e => {
          setNextOwnerId(e.target.value);
        }}
        className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
        aria-label="New owner"
        disabled={mutation.isPending || usersQuery.isLoading}
      >
        <option value="">{usersQuery.isLoading ? 'Loading users…' : 'Select a user…'}</option>
        {users.map(user => (
          <option key={user.id} value={user.id}>
            {user.githubLogin} ({user.id.slice(0, 8)}…)
          </option>
        ))}
      </select>
      <Button
        variant="outline"
        size="sm"
        disabled={mutation.isPending || !nextOwnerId.trim()}
        onClick={handleTransfer}
        className="inline-flex w-fit items-center gap-1.5"
      >
        <UserCog className="size-4" />
        {mutation.isPending ? 'Transferring…' : 'Transfer ownership'}
      </Button>
      {usersQuery.isError && (
        <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
          Failed to load users: {usersQuery.error.message}
        </div>
      )}
      {error && (
        <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
          {error}
        </div>
      )}
    </div>
  );
}
